import React, { useEffect, useMemo, useState } from "react";
import {
  TableContainer,
  Paper,
  Chip,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AppointmentStore from "../../../stores/Appointment/appointmentStore";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import useStore from "../../../helpers/useStore";
import { Event } from "../../../molecules/Calendar/Calendar.type";
import { VisitTypeBadge } from "../../../atoms/Chip";
import { AppointmentCard } from "../UnscheduledAppointments";
import notificationStore from "../../../stores/Notification/notificationStore";
import { updateReceivedAppointment } from "../../../helpers/calendar";
import { AppointmentDto, UserDto, WaitListFilters } from "../../../api-client";
import { observer } from "mobx-react";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import SelectableTable from "../../Sales/SelectableTable";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { UserContext } from "../../../stores/User/User.provider";
import { useCurrentLocation, useLocations } from "../../../stores/User/user.queries";
import { useLocationId } from "../../../stores/User/User.hooks";
import { useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import { useGetWaitListByRxSeries } from "../../../stores/Appointment/appointment.queries";
import { useSaveAppointment } from "../../../stores/Calendar/calendar.mutations";
import {
  useCreateForTreatments,
  useUpdateContinuousAppointment,
} from "../../../stores/Appointment/appointment.mutation";
import { useNavigate } from "react-router-dom";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { CalendarEvent } from "../../../molecules/Calendar/controller/adapters";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import { AccessTime } from "@mui/icons-material";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import GenericInfiniteTable from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import GapPreviewDialog from "./GapPreviewDialog";
import { SublocalityBadge } from "../../PatientOverview/PatientOverview";
import { slotTherapy } from "../../../helpers/slotTherapy";

interface WaitListProps {
  event?: Event;
  handleClose: () => void;
  waitListOpen: boolean;
  calendarEvents: CalendarEvent[];
  onOutdated: () => void;
}

const WaitList = observer((props: WaitListProps) => {
  // console.log("Rendering WaitList with props:", props);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [filters, setFilters] = useState<WaitListFilters>({
    exactLocation: true,
    locations: [],
    waitList: true,
    therapists: [],
    therapistMatch: true,
    exactDuration: true,
    sensibleVisitTypeChange: true,
    // page:1,
    // limit:10
  });
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // const [page, setPage] = useState(1);
  // const [limit, setLimit] = useState(10);
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirmSwapOpen, setConfirmSwapOpen] = useState(false);
  const [pendingSwapSeriesId, setPendingSwapSeriesId] = useState<string | null>(null);
  const CalendarStore = useStore(CalendarContext);
  const { mutateAsync: saveAppointment } = useSaveAppointment();
  const { event, handleClose, waitListOpen, calendarEvents: appointments } = props;
  const [saveLoading, setSaveLoading] = useState(false);
  const todayIso = useMemo(() => new Date().toISOString(), []);
  const navigate = useNavigate();
  const patientsStore = useStore(PatientsContext);
  const { navigateToPatient } = PatientCommands(patientsStore);
  const locationId = useLocationId();

  const {
    data: waitingList,
    isLoading,
    refetch,
  } = useGetWaitListByRxSeries(event!, filters, false, locationId!);

  const [open, setOpen] = useState(false);

  const handleOpenPopup = () => setOpen(true);
  const handleClosePopup = () => setOpen(false);
  const { mutateAsync: createForTreatments } = useCreateForTreatments();

  const { mutate: updateContinuousAppointment } = useUpdateContinuousAppointment();
  // const [entries, setEntries] = useState<any>(waitingList || []);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  //   useEffect(() => {
  //   setPage(1);
  // }, [filters]);

  // loading the dtos of the selected users. Note, that this means that

  const { columnUserIds } = CalendarStore; // selected user ids for calendar columns
  const { data: users = [] } = useUsersByLocation(
    locationId,
    { userIds: columnUserIds } as any,
    true,
    !!locationId,
  );
  let selectedUser: UserDto = users.find((user) => user.id === event?.resourceId!)!;
  const { data: locations } = useLocations();
  const selectedUserLocation = locations?.find((loc) => loc.id === selectedUser?.locationId);

  // Helper to find previous and next appointments
  const getSurroundingAppointments = (event?: Event) => {
    if (!appointments || !event?.start || !event?.resourceId) return { prev: null, next: null };
    const eventTime = new Date(event.start).getTime();
    // console.log("Finding surrounding appointments for event at", event.resourceId, appointments);

    // Filter appointments where any timeslot's userId matches event.resourceId
    const filtered = appointments.filter((ts) => ts?.extendedProps?.user?.id === event.resourceId);

    const sorted = [...filtered]
      .filter((app) => app.start)
      .sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime());

    let prev = null,
      next = null;
    for (let i = 0; i < sorted.length; i++) {
      const appTime = new Date(sorted[i].start!).getTime();
      if (appTime < eventTime) {
        prev = sorted[i];
      } else if (appTime > eventTime) {
        next = sorted[i];
        break;
      }
    }
    return { prev, next };
  };
  const handleConfirmScheduling = () => {
    handleClose();
    if (props.onOutdated) {
      props.onOutdated();
    }
  };
  const handleSelectedIndex = (index: number | ((prev: number | null) => number | null) | null) => {
    // if index is a function, call it with current selectedIndex
    const resolvedIndex = typeof index === "function" ? index(selectedIndex) : index;

    if (resolvedIndex === null || resolvedIndex === undefined) return;

    // console.log("Handle Selected Index:", resolvedIndex);

    setSelectedIndex(resolvedIndex);

    // update the selected row
    const row = waitingList?.data?.[resolvedIndex] ?? null;
    setSelectedAppointment(row);
  };

  useEffect(() => {
    if (selectedUser) {
      const { prev, next } = getSurroundingAppointments(event);
      // console.log("Surrounding Appointments:", prev, next);
      // Check for Institute or Practice for exactLocation
      const hasInstituteOrPractice = [prev, next].some(
        (appt) =>
          appt?.extendedProps?.visitType &&
          ["heim", "praxis"].includes(appt.extendedProps?.visitType.toLowerCase()),
      );

      // Check for Institute for waitList
      const hasInstitute = [prev, next].some(
        (appt) =>
          appt?.extendedProps?.visitType && appt.extendedProps.visitType.toLowerCase() === "heim",
      );
      const gapMinutes = getGapToNextAppointment(event?.start, next);
      setFilters((prevFilters) => ({
        ...prevFilters,
        exactLocation: hasInstituteOrPractice,
        waitList: !hasInstitute,
        therapistMatch: true,
        locations: [
          ...(prev?.extendedProps?.address
            ? [{ ...prev.extendedProps.address, type: prev.extendedProps?.visitType as any }]
            : []),
          ...(next?.extendedProps?.address
            ? [{ ...next.extendedProps.address, type: next.extendedProps?.visitType }]
            : []),
        ].filter((loc, idx, arr) => loc && arr.findIndex((l) => l.id === loc.id) === idx),
        therapists: [selectedUser],
        duration: filters.exactDuration && event?.start ? gapMinutes : undefined,
      }));
      setFiltersInitialized(true);
    }
  }, [selectedUser]);
  useEffect(() => {
    if (filtersInitialized || open) {
      refetch(); // fetch after filters are set
      setFiltersInitialized(false); // prevent future auto-fetch
    }
  }, [filtersInitialized, open]);

  // Call API once when filters are initialized
  // useEffect(() => {
  //   if (filtersInitialized) {
  //     refetch();
  //     setFiltersInitialized(false); // Prevent future calls
  //   }
  // }, [filtersInitialized]);
  // const fetchWaitingListAppointments = async () => {
  //   try {
  //     if (!event?.start) {
  //       notificationStore.showMessage("Event start date is not defined.", "error");
  //       return;
  //     }
  //     setLoading(true);
  //     const data = await getWaitingListByRxSeries(event?.start!, filters);
  //     setEntries(data);
  //   } catch (error) {
  //     console.error("Error fetching waiting list data:", error);
  //     notificationStore.showMessage("Fehler beim Laden der Warteliste.", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const getSlotDurationMinutes = (slot: any): number => {
    return (
      slot?.duration || slotTherapy(slot)?.duration || slot?.visitFee?.duration || 30 // safe fallback
    );
  };

  const buildProposedPreviewAppointment = (
    chosenAppointment: any,
    proposedStart: Date,
    selectedUser: UserDto,
  ) => {
    let cursor = new Date(proposedStart);

    const timeSlots = chosenAppointment.timeSlots?.map((slot: any) => {
      const durationMinutes = getSlotDurationMinutes(slot);
      const slotStart = new Date(cursor);
      const slotEnd = new Date(cursor.getTime() + durationMinutes * 60000);

      cursor = slotEnd;

      return {
        ...slot,
        start: slotStart,
        end: slotEnd,
        user: selectedUser,
      };
    });

    return {
      ...chosenAppointment,
      start: timeSlots?.[0]?.start,
      end: timeSlots?.[timeSlots.length - 1]?.end,
      user: selectedUser,
      timeSlots,
    };
  };

  const [gapPreviewOpen, setGapPreviewOpen] = useState(false);
  const [gapPreviewData, setGapPreviewData] = useState<{
    previous: any | null;
    proposed: any | null;
    next: any | null;
  } | null>(null);

  const columns = [
    {
      key: "patientName",
      header: "Patient",
      // width: 170,
      render: (entry: any) => (
        <Chip
          sx={{ cursor: "pointer" }}
          onClick={() =>
            navigateToPatient(navigate, entry.patientId, null, PATIENT_SUBTABS.APPOINTMENTS)
          }
          label={<Typography fontSize={14}>{entry.patientName}</Typography>}
        />
      ),
    },
    {
      key: "waitList",
      header: "Warteliste",
      // width: 110,
      render: (entry: any) => {
        const patient = entry.chosenAppointment?.timeSlots?.[0]?.therapyRx?.rx?.patient;
        return (
          <Chip
            label={patient?.waitList ? "Ja" : "Nein"}
            color={patient?.waitList ? "primary" : "default"}
            size="small"
          />
        );
      },
    },
    {
      key: "gap",
      header: "Lücke (Tage)",
      // width: 120,
      render: (entry: any) => {
        const valid = entry.existingGapInDays != null && entry.newGapInDays != null;

        const success = valid && entry.existingGapInDays - entry.newGapInDays > 5;
        const gapDiff =
          entry.existingGapInDays != null && entry.newGapInDays != null
            ? entry.existingGapInDays - entry.newGapInDays
            : 0;
        return (
          <Chip
            size="small"
            label={`${entry.existingGapInDays ?? "-"} → ${entry.newGapInDays ?? "-"}`}
            onClick={() => {
              if (!event?.start) return;

              const proposed = buildProposedPreviewAppointment(
                entry.chosenAppointment,
                new Date(event.start),
                selectedUser,
              );

              setGapPreviewData({
                previous: entry.previousAppointment,
                proposed,
                next: entry.nextAppointment,
              });

              setGapPreviewOpen(true);
            }}
            sx={{
              fontSize: 14,
              backgroundColor: success ? mobiliTheme.palette.success.main : undefined,
            }}
          />
        );
      },
    },
    {
      key: "freqCurrent",
      header: "Frequenz - ist",
      // width: 120,
      render: (entry: any) => {
        const stats = entry.scheduledCounts || [];
        const total = stats.reduce((s: number, v: any) => s + (v.count || 0), 0);

        return (
          <Tooltip
            title={
              <pre style={{ margin: 0 }}>
                {stats.map((s: any) => `(${s.rxNumber}) ${s.count} x ${s.abbreviation}`).join("\n")}
              </pre>
            }
          >
            <Chip label={total} size="small" color="primary" />
          </Tooltip>
        );
      },
    },
    {
      key: "freqTarget",
      header: "Frequenz - soll",
      // width: 170,
      render: (entry: any) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {(entry.therapyRxStats || []).map((s: any, i: number) => (
            <Tooltip title={s.rxNumber} key={i}>
              <Chip
                size="small"
                label={`${s.preferredFrequency} x ${s.therapy.abbreviation}`}
                color="primary"
              />
            </Tooltip>
          ))}
          {!entry.therapyRxStats?.length && <Chip size="small" label="-" />}
        </Box>
      ),
    },
    {
      key: "appointment",
      header: "Termin",
      // width: 180,
      render: (entry: any) => <AppointmentCard appointment={entry.chosenAppointment || {}} />,
    },
    {
      key: "action",
      header: "Aktion",
      // width: 180,
      render: (entry: any) => {
        switch (entry.scenario) {
          case "UNSCHEDULED":
            return "Fehlenden Termin planen";
          case "CONTINUOUS":
            return "Neuer Termin";
          case "LAST_SCHEDULED":
            return "Umplanen";
          default:
            return "-";
        }
      },
    },
    {
      key: "address",
      header: "Adresse",
      // width: 250,
      render: (entry: any) => {
        const addr = entry.chosenAppointment?.address?.contactInfo;
        const text = addr ? `${addr.addressLine1 || ""} ${addr.zip || ""}` : "-";

        return (
          <Tooltip title={text}>
            <Chip
              size="small"
              variant="outlined"
              label={
                <>
                  <SublocalityBadge sublocality={entry.chosenAppointment?.address?.sublocality} />{" "}
                  {text}
                </>
              }
              sx={{ maxWidth: 250 }}
            />
          </Tooltip>
        );
      },
    },
  ];

  // const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setFilters({ ...filters, [event.target.name]: event.target.checked });
  // };

  const handleFilterChange = (eventData: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = eventData.target;
    setFilters((prev) => {
      let updated = { ...prev, [name]: checked };
      if (name === "exactDuration") {
        // Always set duration if exactDuration is checked, using gapMinutes
        const { next } = getSurroundingAppointments(event);
        const gapMinutes = getGapToNextAppointment(event?.start, next);
        updated.duration = checked ? gapMinutes : undefined;
      }
      if (name === "sensibleVisitTypeChange" && !checked) {
        // If sensibleVisitTypeChange is unchecked, also uncheck exactLocation
        updated.exactLocation = false;
      }
      return updated;
    });
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      await refetch();
      notificationStore.showMessage("Warteliste aktualisiert.", "success");
    } catch (error) {
      console.error("Error during search:", error);
      notificationStore.showMessage("Fehler bei der Suche.", "error");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleRowClick = (row: any) => {
    setSelectedAppointment(row);
  };

  const handleSelectAppointment = async () => {
    try {
      // Check for missing addressId
      const addressId = selectedAppointment.chosenAppointment?.address?.id;
      if (!addressId) {
        notificationStore.showMessage("Die ausgewählte Planung hat keine Adresse.", "error");
        return;
      }

      // 1. Use unscheduled appointment if available
      if (selectedAppointment.chosenAppointment && selectedAppointment.scenario === "UNSCHEDULED") {
        const newAppointment = updateReceivedAppointment(
          selectedAppointment.chosenAppointment,
          selectedUser.id!,
          event?.start ? new Date(event.start) : new Date(),
          event?.end ? new Date(event.end) : new Date(),
        );
        if (newAppointment) {
          // console.log("New Appointment:", newAppointment);
          const result = event?.continuous
            ? updateContinuousAppointment({
                appointmentId: newAppointment?.id!,
                appointment: newAppointment,
              })
            : await saveAppointment({ id: newAppointment?.id!, appointment: newAppointment });
        }
        handleConfirmScheduling();
        return;
      }

      // 2. If continuous Rx exists on the series
      const hasContinuousRx = selectedAppointment.chosenAppointment?.timeSlots.some(
        (slot: any) => slot.therapyRx?.rx.isPerpetual === true,
      );

      if (hasContinuousRx && selectedAppointment.scenario === "CONTINUOUS") {
        const response: any = await createForTreatments({
          appointmentTemplateDto: {
            therapyRx: selectedAppointment.chosenAppointment?.timeSlots?.map(
              (slot: any) => slot.therapyRx,
            ),
            amount: 1,
            frequency: selectedAppointment.chosenAppointment?.frequency,
            addressId:
              (selectedAppointment.chosenAppointment?.address?.id as string) ||
              selectedUserLocation?.address?.id,
          },
          isContinuous: false,
          patientId:
            selectedAppointment.chosenAppointment?.timeSlots?.[0]?.therapyRx?.rx?.patient?.id,
        });
        const newAppointment = updateReceivedAppointment(
          response[0],
          selectedUser.id!,
          event?.start ? new Date(event.start) : new Date(),
          event?.end ? new Date(event.end) : new Date(),
        );
        if (newAppointment) {
          const result = event?.continuous
            ? updateContinuousAppointment({
                appointmentId: newAppointment?.id!,
                appointment: newAppointment,
              })
            : await saveAppointment({ id: newAppointment?.id!, appointment: newAppointment });
        }
        // Instead of calling swapAppointments directly, open confirmation dialog
        setPendingSwapSeriesId(selectedAppointment.seriesId);
        setConfirmSwapOpen(true);
        return;
      }

      // 3. No continuous Rx: take last appointment in the series and reschedule
      if (
        selectedAppointment.chosenAppointment &&
        selectedAppointment.chosenAppointment.start &&
        selectedAppointment.scenario === "LAST_SCHEDULED"
      ) {
        const newAppointment = updateReceivedAppointment(
          selectedAppointment.chosenAppointment,
          selectedUser.id!,
          event?.start ? new Date(event.start) : new Date(),
          event?.end ? new Date(event.end) : new Date(),
        );
        if (newAppointment) {
          const result = event?.continuous
            ? updateContinuousAppointment({
                appointmentId: newAppointment?.id!,
                appointment: newAppointment,
              })
            : await saveAppointment({ id: newAppointment?.id!, appointment: newAppointment });
        }
        handleConfirmScheduling();
        return;
      }

      notificationStore.showMessage("Kein passendes Szenario für die Planung gefunden.", "warning");
    } catch (error) {
      notificationStore.showMessage("Fehler beim Auswählen des Termins.", "error");
      console.error(error);
    }
  };

  const handleConfirmSwap = async () => {
    if (pendingSwapSeriesId) {
      navigateToPatient(
        navigate,
        selectedAppointment.chosenAppointment.timeSlots[0].therapyRx?.rx?.patient.id,
        null,
        PATIENT_SUBTABS.PRESCRIPTIONS,
        { selectedSeriesId: pendingSwapSeriesId },
      );
      handleConfirmScheduling();
      // setConfirmSwapOpen(false);
      // setPendingSwapSeriesId(null);
      // handleClose();
    }
  };

  const handleCancelSwap = () => {
    setConfirmSwapOpen(false);
    setPendingSwapSeriesId(null);
    handleConfirmScheduling();
  };
  const toolbar = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Chip
          icon={<AccessTime />}
          label={dayjs(event?.start).format("HH:mm")}
          variant="outlined"
          sx={{
            width: 150,
            justifyContent: "center",
          }}
        />

        {/* <TimePicker ampm={false} label="bis" value={dayjs(event?.end)} disabled /> */}

        <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
          <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: "bold", fontSize: "16px" }}>
            Filters:
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.exactLocation}
                  onChange={handleFilterChange}
                  name="exactLocation"
                />
              }
              label="BehandlungsAdresse"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.sensibleVisitTypeChange}
                  onChange={handleFilterChange}
                  name="sensibleVisitTypeChange"
                />
              }
              label="Sensitiver Besuchstyp-Wechsel"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.waitList}
                  onChange={handleFilterChange}
                  name="waitList"
                />
              }
              label="Nur Warteliste"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.therapistMatch}
                  onChange={handleFilterChange}
                  name="therapistMatch"
                />
              }
              label={
                <Chip
                  sx={{
                    position: "relative",
                  }}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: "12px" }}>
                        {selectedUser?.firstName} {selectedUser?.lastName}
                      </Typography>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 14 }}>
                        {selectedUser?.nickName}
                      </Avatar>
                    </Box>
                  }
                />
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.exactDuration}
                  onChange={handleFilterChange}
                  name="exactDuration"
                />
              }
              label="Muss in die Lücke passen"
            />
            <Button
              variant="contained"
              sx={{ ml: 2 }}
              onClick={handleSearch}
              disabled={searchLoading}
            >
              Suchen
            </Button>
          </FormGroup>

          {/* Help Icon with Tooltip */}
          <Button
            size="small"
            startIcon={<HelpOutlineIcon fontSize="small" />}
            onClick={handleOpenPopup}
            sx={{ ml: 1 }}
          >
            Hilfe
          </Button>

          {/* Popup dialog */}
          <Dialog open={open} onClose={handleClosePopup} maxWidth="md" fullWidth>
            <DialogTitle
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              Hinweise zur Lückenfüllung und Warteliste
              <IconButton onClick={handleClosePopup}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Box sx={{ width: "100%", p: 2 }}>
                {/* Behandlungsadresse */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Behandlungsadresse
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Verwendet, um den Standort vorheriger und nächster Termine abzugleichen.</li>
                  <li>Filtert automatisch relevante Standorte, wenn aktiviert.</li>
                </ul>

                {/* Sensitiver Besuchstyp-Wechsel */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Sensitiver Besuchstyp-Wechsel
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Optimiert Termine zwischen Zuhause, Pflegeeinrichtung oder Praxis.</li>
                  <li>
                    Wenn deaktiviert, wird "Behandlungsadresse" ebenfalls automatisch deaktiviert.
                  </li>
                </ul>

                {/* Nur Warteliste */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Nur Warteliste
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Zeigt nur Patienten auf der Warteliste an.</li>
                  <li>Patienten ohne Wartelisten-Eintrag werden ausgeblendet.</li>
                </ul>

                {/* Therapeut */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Therapeut
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Prüft, ob der Therapeut berechtigt ist, die Behandlung durchzuführen.</li>
                  <li>
                    Überprüft auch, ob der Patient bereits von diesem Therapeuten behandelt wurde.
                  </li>
                </ul>

                {/* Filter "Muss in die Lücke passen" */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Filter "Muss in die Lücke passen"
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>
                    Stellt sicher, dass der vorgeschlagene Termin genau in die verfügbare Lücke
                    passt.
                  </li>
                  <li>
                    Berücksichtigt sowohl den Hauptplan als auch die Verfügbarkeit des Patienten.
                  </li>
                  <li>Blockierte Zeitfenster werden automatisch ausgeschlossen.</li>
                </ul>

                {/* Funktionsweise der Terminwahl */}
                <Typography variant="body1" sx={{ mb: 1, fontSize: 16, fontWeight: "bold" }}>
                  Funktionsweise der Terminwahl
                </Typography>

                {/* Ungeplante Termine */}
                <Typography variant="body2" sx={{ mb: 1, fontSize: 15, fontWeight: "bold" }}>
                  Ungeplante Termine
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Werden direkt verwendet, egal ob kontinuierliche oder normale Verordnung.</li>
                </ul>

                {/* Kontinuierliche Verordnungen */}
                <Typography variant="body2" sx={{ mb: 1, fontSize: 15, fontWeight: "bold" }}>
                  Kontinuierliche Verordnungen
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Neuer Termin kann in die Serie eingefügt werden.</li>
                  <li>Die Serie wird bei Bedarf automatisch neu geordnet.</li>
                </ul>

                {/* Geplante Verordnungen */}
                <Typography variant="body2" sx={{ mb: 1, fontSize: 15, fontWeight: "bold" }}>
                  Geplante Verordnungen
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Der letzte Termin der Serie wird umgeplant, um die Lücke zu füllen.</li>
                </ul>

                {/* Interaktion mit der Lücke */}
                <Typography variant="body2" sx={{ mb: 1, fontSize: 15, fontWeight: "bold" }}>
                  Interaktion mit der Lücke (Gap Chip)
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>
                    Klick auf die Lücke zeigt, wie der vorgeschlagene Termin in den Terminplan
                    passt.
                  </li>
                  <li>
                    So lässt sich direkt erkennen, ob die Behandlung gut integriert werden kann.
                  </li>
                </ul>

                {/* Termin auswählen und speichern */}
                <Typography variant="body2" sx={{ mb: 1, fontSize: 15, fontWeight: "bold" }}>
                  Termin auswählen und speichern
                </Typography>
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  <li>Zeile in der Tabelle auswählen, um einen Patienten/Termin zu markieren.</li>
                  <li>Auf "Speichern" klicken, um den Termin einzuplanen.</li>
                  <li>
                    Abhängig vom Szenario wird der Termin direkt verwendet, ein neuer erstellt oder
                    der letzte Termin einer Serie verschoben.
                  </li>
                </ul>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClosePopup} variant="contained">
                Schließen
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>{" "}
    </LocalizationProvider>
  );

  const getGapToNextAppointment = (eventStart?: string | Date, next?: CalendarEvent | null) => {
    if (!eventStart || !next?.start) return undefined;
    const eventDate = new Date(eventStart);
    const nextDate = new Date(next.start);
    const gapMinutes = Math.abs((nextDate.getTime() - eventDate.getTime()) / 60000);
    return gapMinutes;
  };
  return (
    <>
      <Dialog open={waitListOpen} onClose={handleClose} fullScreen>
        <DialogTitle>
          Lücken Füllen für {event?.start ? dayjs(event.start).format("DD.MM.YYYY") : ""}
        </DialogTitle>{" "}
        <DialogContent>
          <GenericInfiniteTable
            data={waitingList?.data || []}
            toolbar={toolbar}
            columns={columns}
            isLoading={isLoading || searchLoading}
            hasMoreData={false}
            fetchMoreData={() => {}}
            selectedIndex={selectedIndex}
            setSelectedIndex={handleSelectedIndex as any}
            onRowClick={(row) => handleRowClick(row)}
          />

          {/* <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={waitingList?.totalPages || 1}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box> */}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={handleClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSelectAppointment} disabled={saveLoading} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmSwapOpen} onClose={handleCancelSwap}>
        <DialogTitle>Terminserie neu ordnen</DialogTitle>
        <DialogContent>
          <Typography>
            Ein neuer Termin wurde erstellt, daher ist es notwendig, die Termine neu zu ordnen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwap} color="inherit">
            Schließen
          </Button>
          <Button onClick={handleConfirmSwap} color="primary" variant="contained">
            Zum Tausch gehen
          </Button>
        </DialogActions>
      </Dialog>
      <GapPreviewDialog
        open={gapPreviewOpen}
        onClose={() => setGapPreviewOpen(false)}
        data={gapPreviewData}
      />
    </>
  );
});

export default WaitList;
